import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { RadioGroup } from '@/components/inputs'
import {
  checkboxLabelSx,
  DENTAL_SPECIALTIES,
  MEDICAL_SPECIALTIES,
  OTHER_HEALTH_SPECIALTIES,
  PROFESSIONAL_CATEGORIES,
  specialtiesForCategories,
  YES_NO_OPTIONS,
} from './constants'
import { RcpQuoteFormData } from './schema'

function toggleValue<T extends string>(
  values: readonly T[],
  value: T,
  checked: boolean,
): T[] {
  if (checked) return [...values, value]

  return values.filter((item) => item !== value)
}

export function StepQuestionario() {
  const { control, getValues, setValue } = useFormContext<RcpQuoteFormData>()
  const categories =
    useWatch({ control, name: 'professional_categories' }) ?? []
  const specialties = useWatch({ control, name: 'specialties' }) ?? []
  const showMedicalSpecialties = categories.includes('medicos')
  const showOtherHealthSpecialties = categories.includes('demais_profissionais')
  const showDentalSpecialties = categories.includes('odontologia')

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Etapa 2 — Questionário</Typography>

      <Controller
        name="professional_categories"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl error={Boolean(error)} component="fieldset">
            <Typography variant="subtitle1" mb={1}>
              Especialidade médica
            </Typography>
            <FormGroup>
              {PROFESSIONAL_CATEGORIES.map((category) => (
                <FormControlLabel
                  key={category.value}
                  control={
                    <Checkbox
                      checked={field.value.includes(category.value)}
                      onChange={(event) => {
                        const nextCategories = toggleValue(
                          field.value,
                          category.value,
                          event.target.checked,
                        )
                        const allowed = new Set(
                          specialtiesForCategories(nextCategories),
                        )

                        field.onChange(nextCategories)
                        setValue(
                          'specialties',
                          getValues('specialties').filter((specialty) =>
                            allowed.has(specialty),
                          ),
                          { shouldValidate: false },
                        )
                      }}
                    />
                  }
                  label={category.label}
                  sx={checkboxLabelSx}
                />
              ))}
            </FormGroup>
            {error?.message && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />

      {(showMedicalSpecialties ||
        showOtherHealthSpecialties ||
        showDentalSpecialties) && (
        <Controller
          name="specialties"
          control={control}
          render={({ field, fieldState: { error } }) => {
            const hasMedicalSpecialty = specialties.some((specialty) =>
              (MEDICAL_SPECIALTIES as readonly string[]).includes(specialty),
            )
            const hasOtherHealthSpecialty = specialties.some((specialty) =>
              (OTHER_HEALTH_SPECIALTIES as readonly string[]).includes(
                specialty,
              ),
            )
            const hasDentalSpecialty = specialties.some((specialty) =>
              (DENTAL_SPECIALTIES as readonly string[]).includes(specialty),
            )

            return (
              <Stack spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  Selecione TODAS as especialidades profissionais da saúde que o
                  proponente atua:
                </Typography>

                {showMedicalSpecialties && (
                  <FormControl
                    error={Boolean(error) && !hasMedicalSpecialty}
                    component="fieldset"
                  >
                    <Typography variant="subtitle1" mb={1}>
                      Médicos
                    </Typography>
                    <SpecialtyOptions
                      options={MEDICAL_SPECIALTIES}
                      value={field.value}
                      onChange={field.onChange}
                    />
                    {error && !hasMedicalSpecialty && (
                      <FormHelperText>
                        Selecione ao menos uma especialidade
                      </FormHelperText>
                    )}
                  </FormControl>
                )}

                {showOtherHealthSpecialties && (
                  <FormControl
                    error={Boolean(error) && !hasOtherHealthSpecialty}
                    component="fieldset"
                  >
                    <Typography variant="subtitle1" mb={1}>
                      Demais profissionais da saúde
                    </Typography>
                    <SpecialtyOptions
                      options={OTHER_HEALTH_SPECIALTIES}
                      value={field.value}
                      onChange={field.onChange}
                    />
                    {error && !hasOtherHealthSpecialty && (
                      <FormHelperText>
                        Selecione ao menos uma especialidade
                      </FormHelperText>
                    )}
                  </FormControl>
                )}

                {showDentalSpecialties && (
                  <FormControl
                    error={Boolean(error) && !hasDentalSpecialty}
                    component="fieldset"
                  >
                    <Typography variant="subtitle1" mb={1}>
                      Profissionais de Odontologia
                    </Typography>
                    <SpecialtyOptions
                      options={DENTAL_SPECIALTIES}
                      value={field.value}
                      onChange={field.onChange}
                    />
                    {error && !hasDentalSpecialty && (
                      <FormHelperText>
                        Selecione ao menos uma especialidade de odontologia
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              </Stack>
            )
          }}
        />
      )}

      <RadioGroup
        control={control}
        name="had_claims_last_5_years"
        label="Nos últimos 5 anos, o proponente sofreu reclamações ou ações judiciais, criminais, administrativas ou arbitrais decorrentes da sua atividade profissional?"
        options={YES_NO_OPTIONS}
        isBooleanChoice
      />

      <RadioGroup
        control={control}
        name="knows_future_claim_risk"
        label="O proponente tem conhecimento de alguma circunstância que possa gerar uma expectativa de reclamação ou ações futuras?"
        options={YES_NO_OPTIONS}
        isBooleanChoice
      />

      <RadioGroup
        control={control}
        name="has_active_professional_registration"
        label="O segurado possui o Registro Profissional ativo no Conselho de Classe Profissional?"
        options={YES_NO_OPTIONS}
        isBooleanChoice
        disclaimerText="Atenção: se na vigência da apólice for constatada ausência ou a inabilitação de Licença ou Registro Profissional, o segurado perderá o direito à indenização."
      />
    </Stack>
  )
}

function SpecialtyOptions({
  options,
  value,
  onChange,
}: {
  options: readonly string[]
  value: string[]
  onChange: (value: string[]) => void
}) {
  return (
    <FormGroup>
      {options.map((specialty) => (
        <FormControlLabel
          key={specialty}
          control={
            <Checkbox
              checked={value.includes(specialty)}
              onChange={(event) => {
                onChange(toggleValue(value, specialty, event.target.checked))
              }}
            />
          }
          label={specialty}
          sx={checkboxLabelSx}
        />
      ))}
    </FormGroup>
  )
}
